import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiNodeTree } from '@remixicon/react';
import {
  CopyButton,
  CopyableValue,
  ExternalLink,
  InfraField,
  InfraSection,
  InfraStack,
  infraCardStyles as styles,
} from '../ui/InfraPrimitives';
import {
  buildLabSetupScript,
  labContextNameFromCluster,
  LabKubectlSection,
  parseLabKubeconfigFromBase64,
} from './labKubectl';

export const EKSClusterInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const environmentName = annotations['infrastructure.backstage.io/environment-name'];
  const templateType = annotations['infrastructure.backstage.io/type'];

  const clusterName = annotations['eks.infrastructure/cluster-name'];
  const clusterEndpoint = annotations['eks.infrastructure/cluster-endpoint'];
  const clusterVersion = annotations['eks.infrastructure/cluster-version'];
  const clusterArn = annotations['eks.infrastructure/cluster-arn'];
  const consoleUrl = annotations['eks.infrastructure/console-url'];
  const nodeGroupName = annotations['eks.infrastructure/node-group-name'];
  const nodeInstanceType = annotations['eks.infrastructure/node-instance-type'];
  const nodeCount = annotations['eks.infrastructure/node-count'];
  const vpcId = annotations['eks.infrastructure/vpc-id'];
  const oidcProviderArn = annotations['eks.infrastructure/oidc-provider-arn'];
  const kubeconfigCommand = annotations['eks.infrastructure/kubeconfig-command'];
  const labAccessNamespace = annotations['eks.infrastructure/lab-access-namespace'];
  const labAccessServiceAccount = annotations['eks.infrastructure/lab-access-service-account'];
  const labKubeconfigBase64 = annotations['eks.infrastructure/lab-kubeconfig-base64'];
  const defaultStorageClass = annotations['eks.infrastructure/default-storage-class'];
  const storageClassType = annotations['eks.infrastructure/storage-class-type'];
  const awsRegion = annotations['aws.infrastructure/region'];

  if (templateType !== 'aws-eks-cluster') {
    return null;
  }

  const labContextName = labContextNameFromCluster(clusterName, environmentName);

  const copyLabSetupScript = () => {
    if (!labKubeconfigBase64) {
      return;
    }
    const parts = parseLabKubeconfigFromBase64(labKubeconfigBase64);
    if (!parts) {
      window.alert(
        'Could not parse lab kubeconfig from this component. Refresh the catalog entry or contact support.',
      );
      return;
    }
    navigator.clipboard.writeText(buildLabSetupScript(parts, labContextName));
  };

  const clusterUrl =
    consoleUrl ||
    `https://console.aws.amazon.com/eks/home?region=${awsRegion}#/clusters/${clusterName}`;

  return (
    <InfoCard
      title="☸️ AWS EKS Cluster"
      subheader={`Environment: ${environmentName || 'N/A'} | Kubernetes: ${clusterVersion || 'N/A'}`}
      icon={<RiNodeTree size={20} />}
    >
      <InfraStack>
        <InfraSection title="🎯 Cluster Information">
          {clusterName && (
            <InfraField
              label="Cluster Name:"
              value={
                <span className={styles.copyRow}>
                  <ExternalLink href={clusterUrl}>{clusterName}</ExternalLink>
                  <CopyButton text={clusterName} />
                </span>
              }
              mono={false}
            />
          )}
          {clusterEndpoint && <CopyableValue label="Cluster Endpoint:" value={clusterEndpoint} />}
          {awsRegion && <InfraField label="Region:" value={awsRegion} />}
          {clusterArn && <CopyableValue label="Cluster ARN:" value={clusterArn} />}
        </InfraSection>

        <InfraSection title="🖥️ Node Group">
          {nodeGroupName && <InfraField label="Node Group Name:" value={nodeGroupName} />}
          {nodeInstanceType && <InfraField label="Instance Type:" value={nodeInstanceType} />}
          {nodeCount && <InfraField label="Node Count:" value={nodeCount} />}
        </InfraSection>

        <InfraSection title="🌐 Network">
          {vpcId && (
            <InfraField
              label="VPC ID:"
              value={
                <span className={styles.copyRow}>
                  <ExternalLink
                    href={`https://console.aws.amazon.com/vpc/home?region=${awsRegion}#VpcDetails:VpcId=${vpcId}`}
                  >
                    {vpcId}
                  </ExternalLink>
                  <CopyButton text={vpcId} />
                </span>
              }
              mono={false}
            />
          )}
          {oidcProviderArn && (
            <CopyableValue label="OIDC Provider ARN:" value={oidcProviderArn} />
          )}
        </InfraSection>

        {(defaultStorageClass || storageClassType) && (
          <InfraSection title="💾 Storage">
            {defaultStorageClass && (
              <InfraField label="Default StorageClass:" value={defaultStorageClass} />
            )}
            {storageClassType && <InfraField label="Storage Type:" value={storageClassType} />}
          </InfraSection>
        )}

        <LabKubectlSection
          cloudLabel="AWS"
          labKubeconfigBase64={labKubeconfigBase64}
          labAccessServiceAccount={labAccessServiceAccount}
          labAccessNamespace={labAccessNamespace}
          labContextName={labContextName}
          onCopySetupScript={copyLabSetupScript}
        />

        {kubeconfigCommand && (
          <InfraSection title="🔧 Optional — AWS CLI access">
            <CopyableValue label="Kubeconfig command:" value={kubeconfigCommand} />
          </InfraSection>
        )}
      </InfraStack>
    </InfoCard>
  );
};
